import { createContext, useContext, type ReactNode } from 'react';
import type { CanvasRowsByNode } from './useCanvasTranslation';

// ReactFlow hands a node component only its `data`; the repaint data travels by context instead.
export type CanvasTranslationContextValue = {
  language: string;
  dir: 'ltr' | 'rtl';
  rowsByNode: CanvasRowsByNode;
  onEditNode?: (nodeId: string) => void;
};

const CanvasTranslationContext = createContext<CanvasTranslationContextValue | null>(null);

// `value` is null in normal English mode, so the provider can wrap the canvas unconditionally.
export function CanvasTranslationProvider({
  value,
  children,
}: {
  value: CanvasTranslationContextValue | null;
  children: ReactNode;
}) {
  return (
    <CanvasTranslationContext.Provider value={value}>{children}</CanvasTranslationContext.Provider>
  );
}

// The translated node type is only mounted while a language is active, so a
// null read is a programming error.
export function useCanvasTranslationContext(): CanvasTranslationContextValue {
  const value = useContext(CanvasTranslationContext);
  if (value === null) {
    throw new Error('useCanvasTranslationContext must be used within CanvasTranslationProvider');
  }
  return value;
}
